/** @format */

import { Injectable, inject } from '@angular/core';
import {
  type Attributes,
  type Span,
  SpanKind,
  SpanStatusCode,
  type Tracer,
  isSpanContextValid,
} from '@opentelemetry/api';

import { TRACER_TOKEN } from './tracer.token';
import { TraceErrorAttributes } from '../interfaces/trace.interface';

@Injectable({
  providedIn: 'root',
})
export class TracerService {
  private readonly tracer: Tracer | null = inject(TRACER_TOKEN, { optional: true });

  private createSpan(tracer: Tracer, name: string, attributes: Attributes): Span {
    return tracer.startSpan(name, {
      kind: SpanKind.CLIENT,
      attributes: attributes, // Attributes let you attach key/value pairs to a Span to add more information about the current operation that its tracking.
    });
  }

  /**
   *
   * tracer.startSpan: Starts a new span without setting it on context.
   * The new span gets set in context and this context is activated for the duration of the function call.
   * @param name
   * @param attributes let you attach key/value pairs to a Span to add more information about the current operation that its tracking.
   * @returns the trace id of the emitted span, or `undefined` when no span was emitted.
   */
  log(name: string, attributes: Attributes): string | undefined {
    if (!this.tracer) return undefined;
    const span = this.createSpan(this.tracer, name, attributes);
    span.setStatus({
      code: SpanStatusCode.OK,
    });
    span.end();

    const spanContext = span.spanContext();

    return isSpanContextValid(spanContext) ? spanContext.traceId : undefined;
  }

  /**
   *
   * Sets exception as a span event
   * @param traceError let you attach key/value pairs to a Span to add more information about the current operation that its tracking.
   */
  logError(traceError: TraceErrorAttributes) {
    if (!this.tracer) return;
    const span = this.createSpan(this.tracer, traceError.name || 'error', traceError);
    span.setStatus({ code: SpanStatusCode.ERROR, message: traceError.message });
    span.recordException(traceError.message);
    span.end();
  }
}
